import { Component, Inject, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { TenantService } from '../../../services/tenant.service';
import { ModuleService } from '../../../services/module.service';
import { ToastService } from '../../../services/toast.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { CreateTenantRequest, UpdateTenantRequest, Tenant } from '../../../models/tenant.model';
import { TenantModule } from '../../../models/module.model';
import { MaterialModule } from '../../../shared/material.module';
import { TenantModuleAssignmentComponent } from '../tenant-module-assignment/tenant-module-assignment.component';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { SearchCountryField, CountryISO } from 'ngx-intl-tel-input';
import { Subscription } from 'rxjs';

export enum DialogMode {
  CREATE = 'create',
  EDIT = 'edit',
  VIEW = 'view'
}

export interface TenantDialogData {
  mode: DialogMode;
  tenant?: Tenant;
}

@Component({
  selector: 'app-tenant-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, NgxIntlTelInputModule],
  templateUrl: './tenant-dialog.component.html',
  styleUrls: ['./tenant-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TenantDialogComponent implements OnInit, OnDestroy {
  tenantForm: FormGroup;
  isLoading = false;
  assignedModules: TenantModule[] = [];
  private phoneNumberSubscription?: Subscription;
  
  // ngx-intl-tel-input configuration
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedArabEmirates, CountryISO.UnitedKingdom];
  separateDialCode = true;
  selectedCountryISO: CountryISO = CountryISO.UnitedStates;

  // URL validator function
  private urlValidator(control: any) {
    if (!control.value || control.value === '') {
      return null; // Empty is valid (optional field)
    }
    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    return urlPattern.test(control.value) ? null : { invalidUrl: true };
  }

  // Clean phone number - remove dashes, spaces, and other separators
  private cleanPhoneNumber(phone: string): string {
    if (!phone) return '';
    // Remove all non-digit characters except leading +
    return phone.replace(/[^\d+]/g, '').replace(/^\++/, '');
  }

  // Map country code to CountryISO
  private getCountryISOFromCode(countryCode: string): CountryISO {
    const codeMap: { [key: string]: CountryISO } = {
      '+1': CountryISO.UnitedStates,
      '+971': CountryISO.UnitedArabEmirates,
      '+44': CountryISO.UnitedKingdom,
      '+880': CountryISO.Bangladesh,
      '+91': CountryISO.India,
      '+92': CountryISO.Pakistan,
      '+966': CountryISO.SaudiArabia,
      '+974': CountryISO.Qatar,
      '+965': CountryISO.Kuwait,
      '+973': CountryISO.Bahrain,
      '+968': CountryISO.Oman,
      '+20': CountryISO.Egypt,
      '+27': CountryISO.SouthAfrica,
      '+33': CountryISO.France,
      '+49': CountryISO.Germany,
      '+39': CountryISO.Italy,
      '+34': CountryISO.Spain,
      '+61': CountryISO.Australia,
      '+86': CountryISO.China,
      '+81': CountryISO.Japan,
      '+82': CountryISO.SouthKorea,
      '+65': CountryISO.Singapore,
      '+60': CountryISO.Malaysia,
      '+66': CountryISO.Thailand,
      '+84': CountryISO.Vietnam,
      '+62': CountryISO.Indonesia,
      '+63': CountryISO.Philippines,
    };
    return codeMap[countryCode] || CountryISO.UnitedStates;
  }

  // Handle phone number change from intl-tel-input
  onPhoneNumberChange(): void {
    const phoneControl = this.tenantForm.get('phoneNumber');
    if (phoneControl && phoneControl.value) {
      const phoneNumber = phoneControl.value;
      // Extract dial code and national number separately
      let countryCode = '';
      let phoneNumberOnly = '';
      
      if (phoneNumber.dialCode) {
        // Ensure country code has a single "+" prefix
        const dialCode = phoneNumber.dialCode.toString();
        countryCode = dialCode.startsWith('+') ? dialCode : '+' + dialCode;
      }
      
      if (phoneNumber.nationalNumber) {
        // Use national number (phone without country code) and clean it
        phoneNumberOnly = this.cleanPhoneNumber(phoneNumber.nationalNumber);
      } else if (phoneNumber.number) {
        // Fallback to full number if nationalNumber not available, then clean it
        phoneNumberOnly = this.cleanPhoneNumber(phoneNumber.number);
      }
      
      this.tenantForm.patchValue({
        contactPhoneCountryCode: countryCode,
        contactPhone: phoneNumberOnly
      }, { emitEvent: false });
    } else {
      // Clear both fields if phone number is empty
      this.tenantForm.patchValue({
        contactPhoneCountryCode: '',
        contactPhone: ''
      }, { emitEvent: false });
    }
  }


  constructor(
    private fb: FormBuilder,
    private tenantService: TenantService,
    private moduleService: ModuleService,
    private toastService: ToastService,
    private errorHandler: ErrorHandlerService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<TenantDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TenantDialogData
  ) {
    this.tenantForm = this.fb.group({
      name: ['', [Validators.required]],
      slug: ['', [Validators.required]],
      description: [''],
      contactEmail: ['', [Validators.required, Validators.email]],
      phoneNumber: [''],
      contactPhone: [''],
      contactPhoneCountryCode: [''],
      address: [''],
      city: [''],
      country: [''],
      allowedSystemUser: [0, [Validators.required, Validators.min(-1)]],
      allowedContractedUser: [0, [Validators.required, Validators.min(-1)]],
      unlimitedSystemUser: [false],
      unlimitedContractedUser: [false],
      website: ['', [this.urlValidator.bind(this)]],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    const tenant = this.data.tenant;
    if (tenant) {
      // Handle phone number pre-selection for edit mode
      if (tenant.contactPhone) {
        let phoneValue: string = '';
        let phoneNumberOnly: string = tenant.contactPhone;
        let countryCode: string = tenant.contactPhoneCountryCode || '';
        
        // Clean the phone number first - remove dashes, spaces, etc.
        phoneNumberOnly = this.cleanPhoneNumber(phoneNumberOnly);
        
        // When separateDialCode is true, we only pass the phone number (without country code)
        // The component will handle the country code separately via the country selector
        if (tenant.contactPhoneCountryCode) {
          // We have country code and phone number separately
          // Remove country code digits from the beginning of contactPhone if present
          const countryCodeWithoutPlus = tenant.contactPhoneCountryCode.replace(/^\+/, '');
          
          // Check if phone number starts with country code digits
          if (phoneNumberOnly.startsWith(countryCodeWithoutPlus)) {
            // Remove the country code from the beginning
            phoneNumberOnly = phoneNumberOnly.substring(countryCodeWithoutPlus.length);
          }
          
          // Set the selected country based on country code
          this.selectedCountryISO = this.getCountryISOFromCode(tenant.contactPhoneCountryCode);
          
          // For separateDialCode=true, pass only the clean phone number (national number)
          // The component will display country code separately
          phoneValue = phoneNumberOnly;
        } else if (tenant.contactPhone.startsWith('+')) {
          // Phone number already includes country code - extract it
          const cleanedPhone = this.cleanPhoneNumber(tenant.contactPhone);
          const match = cleanedPhone.match(/^(\+\d{1,4})(.+)/);
          if (match) {
            countryCode = match[1];
            phoneNumberOnly = match[2];
            // Set the selected country based on country code
            this.selectedCountryISO = this.getCountryISOFromCode(countryCode);
            // Pass only the phone number, not the country code
            phoneValue = phoneNumberOnly;
          } else {
            // If we can't extract, use as-is but try to remove leading +
            phoneValue = cleanedPhone.replace(/^\++/, '');
            phoneNumberOnly = phoneValue;
          }
        } else {
          // Just phone number without country code
          phoneValue = phoneNumberOnly;
          phoneNumberOnly = phoneNumberOnly;
        }
        
        this.tenantForm.patchValue({
          phoneNumber: phoneValue,
          contactPhone: phoneNumberOnly,
          contactPhoneCountryCode: countryCode
        });
      }
      
      // Patch other tenant values (-1 means unlimited)
      const sysUser = (tenant as any).allowedSystemUser;
      const contUser = (tenant as any).allowedContractedUser;
      this.tenantForm.patchValue({
        name: tenant.name,
        slug: tenant.slug,
        description: tenant.description || '',
        contactEmail: tenant.contactEmail || '',
        address: tenant.address || '',
        city: tenant.city || '',
        country: tenant.country || '',
        website: tenant.website || '',
        isActive: tenant.isActive !== undefined ? tenant.isActive : true,
        allowedSystemUser: sysUser === -1 ? -1 : (sysUser ?? 0),
        allowedContractedUser: contUser === -1 ? -1 : (contUser ?? 0),
        unlimitedSystemUser: sysUser === -1,
        unlimitedContractedUser: contUser === -1
      });
      if (sysUser === -1) {
        this.tenantForm.get('allowedSystemUser')?.disable();
      }
      if (contUser === -1) {
        this.tenantForm.get('allowedContractedUser')?.disable();
      }
      
      if (this.isReadOnly) {
        this.tenantForm.disable();
      }
      
      // Load assigned modules for view mode
      if (this.data.mode === DialogMode.VIEW) {
        this.loadAssignedModules();
      }
    }
    
    // Subscribe to phone number changes
    const phoneControl = this.tenantForm.get('phoneNumber');
    if (phoneControl) {
      this.phoneNumberSubscription = phoneControl.valueChanges.subscribe(() => {
        this.onPhoneNumberChange();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.phoneNumberSubscription) {
      this.phoneNumberSubscription.unsubscribe();
    }
  }

  get dialogTitle(): string {
    switch (this.data.mode) {
      case DialogMode.CREATE:
        return 'Create New Tenant';
      case DialogMode.EDIT:
        return 'Edit Tenant';
      case DialogMode.VIEW:
        return 'View Tenant';
      default:
        return 'Tenant';
    }
  }

  get isReadOnly(): boolean {
    return this.data.mode === DialogMode.VIEW;
  }

  onSave(): void {
    if (this.tenantForm.valid) {
      this.isLoading = true;
      
      if (this.data.mode === DialogMode.EDIT && this.data.tenant) {
        this.updateTenant();
      } else if (this.data.mode === DialogMode.CREATE) {
        this.createTenant();
      }
    }
  }

  private createTenant(): void {
    const formValue = this.tenantForm.getRawValue();
    // Remove phoneNumber and unlimited checkboxes from request
    const { phoneNumber, unlimitedSystemUser, unlimitedContractedUser, ...createData } = formValue;
    // -1 = unlimited, otherwise ensure non-negative number
    createData.allowedSystemUser = createData.allowedSystemUser === -1 ? -1 : (Number(createData.allowedSystemUser) || 0);
    createData.allowedContractedUser = createData.allowedContractedUser === -1 ? -1 : (Number(createData.allowedContractedUser) || 0);
    // Remove contactPhoneCountryCode if it's empty to avoid validation issues
    if (!createData.contactPhoneCountryCode || createData.contactPhoneCountryCode.trim() === '') {
      delete createData.contactPhoneCountryCode;
    }
    this.tenantService.createTenant(createData as CreateTenantRequest).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastService.success('Tenant Created', 'Tenant has been created successfully');
        this.dialogRef.close(true); // Return true to indicate success
      },
      error: (error: any) => {
        this.isLoading = false;
        const errorResult = this.errorHandler.handleError(error, this.tenantForm);
        const isValidationError = errorResult.statusCode === 400 && (errorResult.validationErrors?.length ?? 0) > 0;
        this.toastService.error(isValidationError ? 'Validation Error' : 'Error', errorResult.message);
        
        // Show unmapped validation errors if any
        const unmappedErrors = this.errorHandler.getUnmappedValidationErrors(errorResult.validationErrors || [], this.tenantForm);
        if (unmappedErrors.length > 0) {
          const errorMessages = unmappedErrors.map(e => `${e.field}: ${e.message}`).join(' ');
          this.toastService.error('Validation Error', errorMessages);
        }
      }
    });
  }

  private updateTenant(): void {
    const formValue = this.tenantForm.getRawValue();
    // Remove phoneNumber and unlimited checkboxes from request
    const { phoneNumber, unlimitedSystemUser, unlimitedContractedUser, ...updateData } = formValue;
    updateData.allowedSystemUser = updateData.allowedSystemUser === -1 ? -1 : (Number(updateData.allowedSystemUser) || 0);
    updateData.allowedContractedUser = updateData.allowedContractedUser === -1 ? -1 : (Number(updateData.allowedContractedUser) || 0);
    // Remove contactPhoneCountryCode if it's empty to avoid validation issues
    if (!updateData.contactPhoneCountryCode || updateData.contactPhoneCountryCode.trim() === '') {
      delete updateData.contactPhoneCountryCode;
    }
    this.tenantService.updateTenant(this.data.tenant!.id, updateData as UpdateTenantRequest).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastService.success('Tenant Updated', 'Tenant has been updated successfully');
        this.dialogRef.close(true); // Return true to indicate success
      },
      error: (error: any) => {
        this.isLoading = false;
        const errorResult = this.errorHandler.handleError(error, this.tenantForm);
        const isValidationError = errorResult.statusCode === 400 && (errorResult.validationErrors?.length ?? 0) > 0;
        this.toastService.error(isValidationError ? 'Validation Error' : 'Error', errorResult.message);
        
        // Show unmapped validation errors if any
        const unmappedErrors = this.errorHandler.getUnmappedValidationErrors(errorResult.validationErrors || [], this.tenantForm);
        if (unmappedErrors.length > 0) {
          const errorMessages = unmappedErrors.map(e => `${e.field}: ${e.message}`).join(' ');
          this.toastService.error('Validation Error', errorMessages);
        }
      }
    });
  }

  onUnlimitedSystemUserChange(unlimited: boolean): void {
    const control = this.tenantForm.get('allowedSystemUser');
    if (unlimited) {
      control?.setValue(-1, { emitEvent: false });
      control?.disable();
    } else {
      control?.enable();
      control?.setValue(0, { emitEvent: false });
    }
  }

  onUnlimitedContractedUserChange(unlimited: boolean): void {
    const control = this.tenantForm.get('allowedContractedUser');
    if (unlimited) {
      control?.setValue(-1, { emitEvent: false });
      control?.disable();
    } else {
      control?.enable();
      control?.setValue(0, { emitEvent: false });
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  loadAssignedModules(): void {
    if (!this.data.tenant?.id) return;
    
    this.moduleService.getTenantModules(this.data.tenant.id).subscribe({
      next: (tenantModules: TenantModule[]) => {
        this.assignedModules = tenantModules;
      },
      error: (error: any) => {
        const errorResult = this.errorHandler.handleError(error);
        this.toastService.error('Error', errorResult.message);
      }
    });
  }

  openModuleAssignment(): void {
    if (!this.data.tenant) return;

    const dialogRef = this.dialog.open(TenantModuleAssignmentComponent, {
      width: '1200px',
      maxWidth: '95vw',
      disableClose: true,
      data: { tenant: this.data.tenant }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Reload assigned modules after assignment changes
        this.loadAssignedModules();
      }
    });
  }
}